import * as React from "react"

import { Button } from "@/components/ui/button"
import { Combobox } from "@/components/ui/combobox"
import { FormField } from "@/components/ui/form-field"

import {
  toPersonPickerOption,
  UNROUTABLE_PRIMARY_CLIENT_CONTACT_LABELS,
  type PrimaryClientContact,
  type StaffOption,
  type UserId,
} from "./clients-data"

function describePrimaryClientContactRouting(
  primaryClientContact: PrimaryClientContact | null,
): string | undefined {
  if (primaryClientContact === null) return "New reviews arrive unassigned."
  if (primaryClientContact.status === "active") {
    return "New reviews for this engagement are assigned to this person."
  }
  if (primaryClientContact.status === "unresolved") return undefined
  return "New reviews arrive unassigned until you pick someone else."
}

function describePrimaryClientContactPickerPlaceholder(
  primaryClientContact: PrimaryClientContact | null,
): string {
  if (primaryClientContact === null) return "Search staff"
  if (primaryClientContact.status === "active" || primaryClientContact.status === "unresolved") {
    return primaryClientContact.name
  }
  const unroutableLabel = UNROUTABLE_PRIMARY_CLIENT_CONTACT_LABELS[primaryClientContact.status]
  return `${primaryClientContact.name} (${unroutableLabel.toLowerCase()})`
}

const ignoreFailureAlreadyReportedByPage = () => undefined

export function PrimaryClientContactControl({
  primaryClientContact,
  primaryClientContactCandidates,
  onPrimaryClientContactChange,
}: {
  primaryClientContact: PrimaryClientContact | null
  primaryClientContactCandidates: readonly StaffOption[]
  onPrimaryClientContactChange: (primaryClientContactId: UserId | null) => Promise<void>
}): JSX.Element {
  const [isChangingPrimaryClientContact, setIsChangingPrimaryClientContact] = React.useState(false)

  const primaryClientContactOptions = primaryClientContactCandidates.map(toPersonPickerOption)

  const changePrimaryClientContact = async (primaryClientContactId: UserId | null) => {
    setIsChangingPrimaryClientContact(true)
    await onPrimaryClientContactChange(primaryClientContactId).catch(
      ignoreFailureAlreadyReportedByPage,
    )
    setIsChangingPrimaryClientContact(false)
  }

  return (
    <FormField
      label="Primary Client Contact"
      hint={describePrimaryClientContactRouting(primaryClientContact)}
      className="sm:max-w-[420px]"
    >
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <Combobox
            options={primaryClientContactOptions}
            value={primaryClientContact?.userId ?? null}
            onValueChange={(pickedUserId) => {
              void changePrimaryClientContact(pickedUserId as UserId | null)
            }}
            placeholder={describePrimaryClientContactPickerPlaceholder(primaryClientContact)}
            searchPlaceholder="Search by name or email"
            disabled={isChangingPrimaryClientContact}
          />
        </div>
        {primaryClientContact !== null && (
          <Button
            size="sm"
            variant="ghost"
            disabled={isChangingPrimaryClientContact}
            onClick={() => {
              void changePrimaryClientContact(null)
            }}
          >
            Clear
          </Button>
        )}
      </div>
    </FormField>
  )
}
